import { Add, Sync } from "@mui/icons-material";
import { Button, CircularProgress, MenuItem, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import CampaignActions from "./CampaignActions";
import CampaignDetailDrawer from "./CampaignDetailDrawer";
import CampaignsTable from "./CampaignsTable";
import CreateAdButton from "./CreateAdButton";
import CreateCampaignDialog from "./CreateCampaignDialog";
import StatTile from "./StatTile";
import {
  currencyOf,
  formatCount,
  formatMoney,
  maxSpendCents,
  summarize,
  totalsCurrency,
} from "./marketingFormat";
import { useCampaigns, useMarketingActions } from "./useMarketing";

function startMessage(campaign, currency) {
  const daily =
    campaign.dailyBudgetCents > 0 ? formatMoney(campaign.dailyBudgetCents, currency) : null;
  if (!daily) {
    return `Start "${campaign.name}"? Its budget is set on the ad sets in Meta, and Meta begins spending as soon as it is delivering.`;
  }
  const total = campaign.durationDays
    ? ` for ${campaign.durationDays} days, up to ${formatMoney(
        maxSpendCents(campaign.dailyBudgetCents, campaign.durationDays),
        currency
      )} in total`
    : " until you pause it";
  return `Start "${campaign.name}"? Meta can spend up to ${daily} per day${total}. Spend is charged to your ad account.`;
}

function confirmMessage({ kind, campaign }, currency) {
  return kind === "start"
    ? startMessage(campaign, currency)
    : `Pause "${campaign.name}" on Meta? Delivery stops and no further spend is charged. Spend so far stays.`;
}

function AccountFilter({ connections, value, onChange }) {
  if (connections.length < 2) return null;
  return (
    <TextField
      select
      size="small"
      label="Account"
      value={value ?? "all"}
      onChange={(e) => onChange(e.target.value === "all" ? null : e.target.value)}
      sx={{ minWidth: 200 }}
    >
      <MenuItem value="all">All accounts</MenuItem>
      {connections.map((connection) => (
        <MenuItem key={connection.id} value={connection.id}>
          {connection.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

export default function CampaignsPanel({ connections, perms, openId, onOpenChange }) {
  const [scope, setScope] = useState(null);
  const campaigns = useCampaigns(scope);
  const actions = useMarketingActions();
  const [creating, setCreating] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const connected = connections.length > 0;

  const rows = useMemo(
    () => (campaigns.data?.pages || []).flatMap((page) => page?.content || []),
    [campaigns.data]
  );
  const totals = useMemo(() => summarize(rows), [rows]);
  const money = totalsCurrency(connections, rows);
  const busy = syncing || [actions.start, actions.pause, actions.refresh].some((m) => m.isLoading);

  const ask = (kind) => (campaign) => setConfirm({ kind, campaign });
  const runConfirmed = () => {
    const { kind, campaign } = confirm;
    (kind === "start" ? actions.start : actions.pause).mutate(campaign.id, {
      onSettled: () => setConfirm(null),
    });
  };

  const syncAccounts = async () => {
    const targets = scope == null ? connections.map((c) => c.id) : [scope];
    setSyncing(true);
    const results = await Promise.allSettled(targets.map((id) => actions.sync.mutateAsync(id)));
    setSyncing(false);
    const done = results.filter((r) => r.status === "fulfilled").map((r) => r.value?.data?.data);
    if (done.length > 0) {
      const imported = done.reduce((sum, r) => sum + (r?.imported ?? 0), 0);
      const updated = done.reduce((sum, r) => sum + (r?.updated ?? 0), 0);
      toast.success(`Synced from Meta: ${imported} new, ${updated} updated`);
    }
  };

  const rowActions = {
    perms,
    busy,
    onRefresh: (campaign) => actions.refresh.mutate(campaign.id),
    onStart: ask("start"),
    onPause: ask("pause"),
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile label="Running" value={totals.running} />
        <StatTile
          label="Spend"
          value={money.mixed ? "-" : formatMoney(totals.spendCents, money.currency)}
          hint={
            money.mixed
              ? "Accounts use different currencies"
              : campaigns.hasNextPage
              ? "Loaded campaigns only"
              : "All campaigns"
          }
        />
        <StatTile label="Impressions" value={formatCount(totals.impressions)} />
        <StatTile label="Clicks" value={formatCount(totals.clicks)} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <AccountFilter connections={connections} value={scope} onChange={setScope} />
        {perms.canRead ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={syncing ? <CircularProgress size={14} /> : <Sync />}
            disabled={!connected || busy}
            onClick={syncAccounts}
          >
            Sync from Meta
          </Button>
        ) : null}
        {perms.canCreate ? (
          <CreateAdButton connections={connections} defaultConnectionId={scope} />
        ) : null}
        {perms.canCreate ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Add />}
            disabled={!connected}
            onClick={() => setCreating(true)}
          >
            New paused campaign
          </Button>
        ) : null}
      </div>

      {campaigns.isLoading ? <CircularProgress size={24} /> : null}
      {campaigns.isError ? (
        <p className="text-sm text-app-status-cancelled-text">Could not load campaigns.</p>
      ) : null}
      {!campaigns.isLoading && !campaigns.isError && rows.length === 0 ? (
        <div className="rounded border border-dashed border-app-border p-6 text-center text-sm text-app-text-secondary">
          <p className="font-medium text-app-text">No campaigns yet.</p>
          <p>
            {connected
              ? "Create an ad, boost a post from the Posts tab, or use Sync from Meta to bring in campaigns made in Ads Manager."
              : "Connect your Meta account first."}
          </p>
        </div>
      ) : null}
      {rows.length > 0 ? (
        <CampaignsTable
          campaigns={rows}
          connections={connections}
          onOpen={(campaign) => onOpenChange(campaign.id)}
          {...rowActions}
        />
      ) : null}
      {campaigns.hasNextPage ? (
        <Button
          size="small"
          disabled={campaigns.isFetchingNextPage}
          onClick={() => campaigns.fetchNextPage()}
        >
          Load more
        </Button>
      ) : null}

      <CampaignDetailDrawer
        campaignId={openId}
        canReadLeads={perms.canReadLeads}
        connections={connections}
        onClose={() => onOpenChange(null)}
        actions={(campaign) => <CampaignActions campaign={campaign} {...rowActions} />}
      />
      <CreateCampaignDialog
        open={creating}
        connections={connections}
        defaultConnectionId={scope}
        onClose={() => setCreating(false)}
        mutation={actions.createCampaign}
      />
      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.kind === "start" ? "Start campaign" : "Pause campaign"}
        message={
          confirm
            ? confirmMessage(confirm, currencyOf(connections, confirm.campaign.connectionId))
            : ""
        }
        confirmLabel={confirm?.kind === "start" ? "Start spending" : "Pause"}
        confirmColor={confirm?.kind === "start" ? "primary" : "warning"}
        loading={actions.start.isLoading || actions.pause.isLoading}
        onConfirm={runConfirmed}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
